import React, { useState } from 'react'
import ReactDOM from 'react-dom/client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import { createKlearTrpcClient } from '@klear/ui/lib/create-trpc-client'
import { useApiErrorHandler } from '@klear/ui/hooks/use-api-error-handler'
import { AppGate } from '@klear/ui/components/app-gate'
import { useBuildVersionGate } from '@klear/ui/hooks/use-build-version-gate'
import klearLogo from '@/assets/logo-klear.png'
import { getApiUrl, getAppBasePath } from '@klear/runtime-env'
import { legacyCrossHostTarget } from '@klear/ui/lib/legacy-cross-host-target'
import { trpc } from '@klear/ui/lib/trpc'
import { ROUTE_TRADER } from '@klear/constants'
import { traderIdFromPathname } from '@/lib/trader-path'
import { ThemeProvider } from '@klear/ui/contexts/theme-context'
import isString from 'lodash/isString'

import App from '@/App'
import '@klear/ui/styles/globals.css'

ModuleRegistry.registerModules([AllCommunityModule])

export default function Root() {
  const { isMaintenance, onApiError } = useApiErrorHandler(`${getApiUrl()}/health`)
  const { isStale } = useBuildVersionGate()

  const [queryClient] = useState(() => {
    const client = new QueryClient({
      queryCache: new QueryCache({ onError: onApiError }),
      mutationCache: new MutationCache({ onError: onApiError }),
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnMount: true,
          staleTime: 0
        }
      }
    })
    client.setQueryDefaults([['trader', 'get']], { staleTime: 60_000, refetchOnMount: false })
    return client
  })

  const [trpcClient] = useState(() =>
    createKlearTrpcClient({
      apiUrl: getApiUrl(),
      fetch: (input: RequestInfo | URL, init?: RequestInit) => {
        const traderId = traderIdFromPathname(window.location.pathname, getAppBasePath(ROUTE_TRADER))

        const newInit = {
          ...init,
          credentials: 'include' as RequestCredentials
        }

        if (traderId) {
          const urlString = isString(input) ? input : input.toString()
          const urlObj = new URL(urlString, window.location.origin)
          urlObj.searchParams.set('traderId', traderId)
          return fetch(urlObj.toString(), newInit)
        }

        return fetch(input, newInit)
      }
    })
  )

  return (
    <React.StrictMode>
      <ThemeProvider>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AppGate isMaintenance={isMaintenance} isStale={isStale} logoSrc={klearLogo}>
              <App />
            </AppGate>
          </QueryClientProvider>
        </trpc.Provider>
      </ThemeProvider>
    </React.StrictMode>
  )
}

declare global {
  interface Window {
    __klearTraderReactRoot?: ReturnType<typeof ReactDOM.createRoot>
  }
}
const crossHostTarget = legacyCrossHostTarget(window.location)
if (crossHostTarget) {
  window.location.replace(crossHostTarget)
} else {
  const rootEl = document.getElementById('root')!
  const root = window.__klearTraderReactRoot ?? (window.__klearTraderReactRoot = ReactDOM.createRoot(rootEl))
  root.render(<Root />)
}
